import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to manage GoHighLevel dynamic call tracking
 * Handles number replacement after React renders and on route changes
 * 
 * @param {Object} config - GHL configuration
 * @param {string} config.locationId - GHL location ID
 * @param {string} config.poolId - GHL pool ID
 * @param {string} config.type - Pool type (default: 'pool')
 * @param {number} config.maxRetries - Maximum retry attempts (default: 3)
 * @param {number} config.retryDelay - Initial retry delay in ms (default: 500)
 * @param {boolean} config.debug - Enable debug logging (default: false)
 */
const useGHLTracking = ({
  locationId = 'pTpZs5Up0T2OAJPqtcLs',
  poolId = 'N25vpDUezjC9jBRSwq5x',
  type = 'pool',
  maxRetries = 3,
  retryDelay = 500,
  debug = true
}) => {
  const location = useLocation();
  const isProcessingRef = useRef(false);
  const retryTimeoutRef = useRef(null);

  const log = (...args) => {
    if (debug) {
      console.log('[GHL Tracking]', ...args);
    }
  };

  const error = (...args) => {
    console.error('[GHL Tracking Error]', ...args);
  };

  /**
   * Check if GHL scripts are loaded
   */
  const isGHLReady = () => {
    return (
      typeof window !== 'undefined' &&
      window.numberPool &&
      typeof window.numberPool.getNumber === 'function' &&
      typeof window.numberPool.replaceNumber === 'function'
    );
  };

  /**
   * Replace phone numbers with GHL tracked number
   */
  const replacePhoneNumbers = async (attempt = 1) => {
    // Prevent concurrent executions
    if (isProcessingRef.current) {
      log('Already processing, skipping...');
      return;
    }

    isProcessingRef.current = true;
    log(`Attempt ${attempt}/${maxRetries} - Starting number replacement...`);

    try {
      // Check if GHL is ready
      if (!isGHLReady()) {
        throw new Error('GHL numberPool not available');
      }

      // Get assigned tracking number from GHL
      log('Requesting tracking number from GHL...');
      
      // THE FIX: Destructure the returned object
      const { assignedNumber, forwardingNumber } = await window.numberPool.getNumber(
        locationId, 
        poolId, 
        type
      );

      if (!assignedNumber || !forwardingNumber) {
        throw new Error('Invalid response from GHL');
      }

      log('✅ Received tracking number:', JSON.stringify(assignedNumber, null, 2));
      log('📞 Forwarding numbers to replace:', JSON.stringify(forwardingNumber, null, 2));

      // Replace each forwarding number with the assigned tracking number
      forwardingNumber.forEach(num => {
        log(`Replacing ${num.national} with ${assignedNumber.national}...`);
        window.numberPool.replaceNumber(assignedNumber, num);
      });

      log('✅ Successfully replaced all phone numbers on page');
      isProcessingRef.current = false;
      return true;

    } catch (err) {
      error(`Attempt ${attempt} failed:`, err.message);

      // Retry logic
      if (attempt < maxRetries) {
        const delay = retryDelay * Math.pow(1.5, attempt - 1); // Exponential backoff
        log(`Retrying in ${delay}ms...`);

        retryTimeoutRef.current = setTimeout(() => {
          isProcessingRef.current = false;
          replacePhoneNumbers(attempt + 1);
        }, delay);
      } else {
        error(`❌ Failed after ${maxRetries} attempts. Phone numbers will display default number.`);
        isProcessingRef.current = false;
      }

      return false;
    }
  };

  /**
   * Initialize GHL tracking
   */
  const initializeTracking = () => {
    log('🔄 Route changed to:', location.pathname);
    
    // Clear any pending retries
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Reset processing flag
    isProcessingRef.current = false;

    // Wait for React to finish rendering before replacing numbers
    requestAnimationFrame(() => {
      setTimeout(() => {
        replacePhoneNumbers();
      }, 300); // Slightly longer delay for complex pages
    });
  };

  // Effect to trigger on route changes
  useEffect(() => {
    initializeTracking();

    // Cleanup function
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Return utility functions for manual control if needed
  return {
    replaceNumbers: replacePhoneNumbers,
    isReady: isGHLReady
  };
};

export default useGHLTracking;